<template>
  <div class="containers-single">
    <SectionHeader>
      <template v-slot:breadcrumbs>
        <BreadCrumbs
          :items="[{ text: $t('meta.containerGroups'), name: 'container_groups' }, { text: containerGroup.name }]"
        />
      </template>
    </SectionHeader>

    <div v-if="loading">
      <LoadingSpinner full />
    </div>
    <div v-else>
      <div class="body-content">
        <div class="content-wrap container-group-detail">
          <header class="content-header cf">
            <h3>{{ $t('meta.baseData') }}</h3>
          </header>
          <div class="content-body">
            <div class="row row-gutter-20">
              <div class="col-12">
                <h3>{{ containerGroup.name }}</h3>
                <p v-if="containerGroup.preformatted">
                  <span class="material-icons">gps_fixed</span><span>{{ containerGroup.preformatted }}</span>
                </p>
                <p>
                  <span class="material-icons">delete</span
                  ><span>{{ containerGroup.container_count }} {{ $t('meta.containers') }}</span>
                </p>
                <p v-if="containerGroup.company">
                  <span class="material-icons">business</span><span>{{ containerGroup.company.name }}</span>
                </p>
                <p v-if="containerGroup.project">
                  <span class="material-icons">all_inbox</span><span>{{ containerGroup.project.name }}</span>
                </p>
              </div>
              <div class="col-12">
                <ContainerMap :container="containerGroup" height="250px" />
              </div>
            </div>
          </div>
        </div>

        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('containerGroups.allForLocation') }}</h3>
          </header>
          <div class="content-body group-body">
            <div v-for="container in containers" :key="container.uuid" class="container-group">
              <h3>
                <Link :to="`/containers/${container.uuid}`"> {{ container.name }}</Link>
              </h3>
              <p class="grp-type">{{ container.type_name }}</p>
              <p class="grp-empty">
                {{ $t('containers.emptyingDates') }}:
                <span v-for="tpl in container.empty_templates" :key="tpl.uuid" class="grp-emptying-description">
                  <EmptyingTemplateDescription
                    :repeatType="tpl.repeat_type"
                    :repeatInterval="tpl.repeat_interval"
                    :referenceDate="tpl.reference_date"
                  />
                </span>
              </p>

              <div class="group-empty-events cf">
                <h4>{{ $t('containerGroups.emptyingTimes') }}</h4>
                <div v-for="manual_value in container.manual_values" :key="manual_value.uuid">
                  <h5 v-if="manual_value.fill_level > 75" style="color: #e3204f">{{ manual_value.fill_level }}%</h5>
                  <h5 v-else-if="manual_value.fill_level > 25" style="color: #f5a623">
                    {{ manual_value.fill_level }}%
                  </h5>
                  <h5 v-else-if="manual_value.fill_level <= 25" style="color: #50c37c">
                    <span v-if="manual_value.fill_level === -100" style="color: #444"> NG </span>
                    <span v-else> {{ manual_value.fill_level }}% </span>
                  </h5>
                  <h6>
                    {{
                      $t('meta.atDate', {
                        date: manual_value.measured_at_formatted,
                      })
                    }}
                  </h6>
                </div>
              </div>

              <div class="group-empty-events cf">
                <h4>{{ $t('meta.routes') }}</h4>
                <span v-for="route_stop in container.route_stops" :key="route_stop.container.uuid">
                  <div v-if="route_stop.route">
                    <Link :to="`/routes/${route_stop.route.uuid}`">
                      <span v-if="route_stop.container">
                        <span v-if="route_stop.container.status === 'pending'" class="stop-st st-pending material-icons"
                          >schedule</span
                        >
                        <span v-if="route_stop.container.status === 'emptied'" class="stop-st st-emptied material-icons"
                          >check_circle</span
                        >
                        <span v-if="route_stop.container.status === 'skipped'" class="stop-st st-skipped material-icons"
                          >schedule</span
                        >
                        <span v-if="route_stop.container.status === null" class="stop-st st-pending material-icons"
                          >schedule</span
                        >
                      </span>
                      <span v-if="route_stop.route" style="font-size: 14px">{{ route_stop.route.name }}</span>
                    </Link>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadCrumbs from '@/components/BreadCrumbs'
import EmptyingTemplateDescription from '@/components/EmptyingTemplates/Description.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import ContainerMap from '@/components/container/ContainerMap'
import dateUtils from '@/components/mixins/dateUtils'

import containersApi from '@/api/container'
import containerGroupsApi from '@/api/containerGroups'

export default {
  name: 'container_group',
  components: { BreadCrumbs, ContainerMap, EmptyingTemplateDescription, LoadingSpinner, SectionHeader },
  mixins: [dateUtils],
  data: () => ({
    loading: true,
    containerGroup: {},
    containers: [],
  }),
  async mounted() {
    this.containerGroup = (await containerGroupsApi.get(this.$route.params.uuid)).containerGroup

    this.containers = await Promise.all(
      this.containerGroup.containers.map(async ({ uuid }) => {
        const container = await containersApi.get(uuid)

        container.manual_values = await containersApi.getManualValues(uuid)
        container.route_stops = await containersApi.getRouteStops(uuid)

        return container
      }),
    )

    this.loading = false
  },
}
</script>

<style lang="scss">
.container-group-detail {
  h3 {
    margin: 0 0 14px 0;
    color: #314053;
    font-weight: 600;

    a {
      color: #314053;
    }
  }

  p {
    margin: 7px 0;

    span {
      display: inline-block;
      vertical-align: middle;
    }

    .material-icons {
      margin-right: 8px;
      font-size: 20px;
      width: 22px;
      text-align: center;
    }
  }
}

.st-pending {
  color: #ffba00;
}

.st-emptied {
  color: #1c9600;
}

.group-empty-events {
  border: 1px dashed #e3e3e3;
  padding: 20px 20px 10px 20px;
  border-radius: var(--border-radius-m);
  margin-bottom: 10px;

  h4 {
    margin: 0 0 20px 0;
    font-size: 18px;
    font-weight: 600;
  }

  div {
    width: 170px;
    margin-right: 10px;
    float: left;
    background: #f9f9f9;
    border: 1px solid #f1f1f1;
    padding: 14px;
    text-align: center;
    margin-bottom: 10px;

    .stop-st {
      text-align: center;
      display: block;
      font-size: 24px;
      margin-bottom: 5px;
    }

    a {
      color: #444;
      display: block;
    }

    h5 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    h6 {
      margin: 5px 0 0 0;
      font-weight: 400;
      font-size: 14px;
    }
  }
}

.content-wrap .content-body.group-body {
  padding: 0;

  .container-group {
    padding: 30px 20px;
    border-bottom: 1px solid #f1f1f1;

    h3 {
      margin: 0 0 6px 0;
      font-weight: 600;
      a {
        color: #314053;
      }
    }

    .grp-type {
      margin: 0 0 6px 0;
    }

    .grp-empty {
      margin: 0 0 20px 0;
    }

    .grp-emptying-description {
      padding-left: 0.5rem;
    }

    .grp-emptying-description + .grp-emptying-description:before {
      content: '|';
      position: relative;
      left: -0.25rem;
    }
  }
}
</style>
